import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Send, MessageCircle, MapPin, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { footerLinks } from '../data/mock';

export default function ContactPage() {
  const formRef = useRef(null);
  const formInView = useInView(formRef, { once: true, margin: '-50px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSending(true);
    await new Promise((r) => setTimeout(r, 1500));
    toast.success("Message sent successfully! We'll get back to you shortly.");
    setFormData({ name: '', email: '', phone: '', message: '' });
    setSending(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email Us',
      value: footerLinks.contact.email,
      href: `mailto:${footerLinks.contact.email}`,
    },
    {
      icon: Phone,
      label: 'Call Us',
      value: footerLinks.contact.phone,
      href: `tel:${footerLinks.contact.phone}`,
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: footerLinks.contact.address,
      href: null,
    },
  ];

  return (
    <main className="w-full max-w-full overflow-x-hidden">
      <section className="relative overflow-hidden pt-32 sm:pt-36 pb-14 sm:pb-16">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[320px] sm:w-[500px] h-[280px] sm:h-[400px] bg-blue-600/6 rounded-full blur-[100px] sm:blur-[120px]" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">Contact</span>
            <h1 className="text-[2.5rem] sm:text-5xl lg:text-6xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6 leading-tight break-words">
              Let&apos;s Start a
              <br />
              <span className="text-blue-400">Conversation</span>
            </h1>
            <p className="text-base sm:text-lg text-[var(--text-muted)] max-w-2xl leading-relaxed">
              Have a project in mind? We&apos;d love to hear about it. Get in touch and let&apos;s explore how we can help your business grow.
            </p>
          </motion.div>
        </div>
      </section>

      <section ref={formRef} className="relative pb-24 overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3 min-w-0"
            >
              <div className="p-6 sm:p-8 md:p-10 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)] transition-colors duration-300">
                <h2 className="text-2xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6">Send a Message</h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2 min-w-0">
                      <Label htmlFor="name" className="text-[var(--text-secondary)] text-sm">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full bg-[var(--surface-2)] border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-blue-500/50 rounded-xl py-3"
                      />
                    </div>

                    <div className="space-y-2 min-w-0">
                      <Label htmlFor="email" className="text-[var(--text-secondary)] text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full bg-[var(--surface-2)] border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-blue-500/50 rounded-xl py-3"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="phone" className="text-[var(--text-secondary)] text-sm">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+8801710000000"
                      className="w-full bg-[var(--surface-2)] border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-blue-500/50 rounded-xl py-3"
                    />
                  </div>

                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="message" className="text-[var(--text-secondary)] text-sm">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={5}
                      className="w-full bg-[var(--surface-2)] border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-blue-500/50 rounded-xl resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sending}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-6 rounded-xl text-base transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/20 w-full md:w-auto"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2 space-y-6 min-w-0"
            >
              <div className="space-y-4">
                {contactInfo.map((item) => {
                  const Icon = item.icon;
                  const Wrapper = item.href ? 'a' : 'div';
                  const wrapperProps = item.href ? { href: item.href } : {};

                  return (
                    <Wrapper
                      key={item.label}
                      {...wrapperProps}
                      className="flex items-start gap-4 p-5 rounded-xl bg-[var(--card-color)] border border-[var(--border-color)] hover:border-blue-500/20 transition-all duration-300 group min-w-0"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-600/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[var(--text-primary)] font-medium text-sm mb-0.5 break-words">{item.label}</div>
                        <div className="text-[var(--text-muted)] text-sm break-words">{item.value}</div>
                      </div>
                    </Wrapper>
                  );
                })}
              </div>

              <a
                href={`https://wa.me/${footerLinks.contact.whatsapp}?text=Hi! I'm interested in your digital services.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 hover:border-[#25D366]/40 transition-all duration-300 group min-w-0"
              >
                <div className="w-10 h-10 rounded-lg bg-[#25D366]/20 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[var(--text-primary)] font-medium text-sm mb-0.5 break-words">Chat on WhatsApp</div>
                  <div className="text-[var(--text-muted)] text-sm break-words">Quick response guaranteed</div>
                </div>
              </a>

              <div className="p-5 rounded-xl bg-[var(--card-color)] border border-[var(--border-color)] min-w-0">
                <h4 className="text-[var(--text-primary)] font-semibold text-sm mb-4">Follow Us</h4>
                <div className="flex gap-3 flex-wrap">
                  {Object.entries(footerLinks.social).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded-lg bg-[var(--surface-2)] text-[var(--text-muted)] hover:text-blue-400 border border-[var(--border-color)] hover:border-blue-500/20 text-sm capitalize transition-all duration-300 break-words"
                    >
                      {platform}
                    </a>
                  ))}
                </div>
              </div>

              <div className="rounded-xl overflow-hidden border border-[var(--border-color)] h-52">
                <iframe
                  title="Your IT Solution Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31747.1553735507!2d90.4337558434558!3d23.819582092209657!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755c62fce7d991f%3A0xacfaf1ac8e944c05!2sBasundhara%20Residential%20Area%2C%20Dhaka!5e1!3m2!1sen!2sbd!4v1772951002032!5m2!1sen!2sbd"
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                    filter: 'invert(90%) hue-rotate(180deg) brightness(0.9) contrast(0.9)',
                  }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}